import React, { useEffect } from 'react';

import {
    annotation,
    INLINE_LINK,
    MARK_BOLD,
    MARK_CODE,
    MARK_ITALIC,
    MARK_STRIKETHROUGH,
    MARK_SUBSCRIPT,
    MARK_SUPERSCRIPT,
} from '@gitbook/doc-core';
import { useHiveClient } from '@gitbook/hive-react';
import { usePaletteHandle } from '@gitbook/react-uikit';
import { type Change, isBlockRangeSelection, TextRange } from '@gitbook/slate';
import { type MarkProps, type Plugin, useEditor } from '@gitbook/slate-react';

import { useContentReferenceContext } from '../../ContentReferences';
import {
    isEditorAnnotation,
    isEditorBold,
    isEditorCode,
    isEditorItalic,
    isEditorLink,
    isEditorStrikethrough,
    isEditorSubscript,
    isEditorSuperscript,
    SELECTION_HIGHLIGHTING_MARK,
} from '../common';
import { createLinkPalette } from '../link/util';

export const formattingPlugin: Plugin = {
    renderMark,
    onKeyDown,
};

function onKeyDown(event: KeyboardEvent, change: Change): Change | undefined {
    const { value } = change;
    const { selection } = value;
    const isInSupportedBlock = !value.isInVoid;

    if (!isInSupportedBlock || isBlockRangeSelection(selection)) {
        return undefined;
    }

    if (isEditorBold(event)) {
        change.toggleMark(MARK_BOLD);
        return change;
    }

    if (isEditorItalic(event)) {
        change.toggleMark(MARK_ITALIC);
        return change;
    }

    if (isEditorCode(event)) {
        change.toggleMark(MARK_CODE);
        return change;
    }

    if (isEditorStrikethrough(event)) {
        change.toggleMark(MARK_STRIKETHROUGH);
        return change;
    }

    if (isEditorSuperscript(event)) {
        change.toggleMark(MARK_SUPERSCRIPT);
        return change;
    }

    if (isEditorSubscript(event)) {
        event.preventDefault();
        change.toggleMark(MARK_SUBSCRIPT);
        return change;
    }

    if (isEditorAnnotation(event) && selection.isExpanded) {
        annotation.wrapWithAnnotation(change);
        return change;
    }

    if (isEditorLink(event)) {
        event.stopPropagation();

        // Ignore if the selection is not expanded but still catch the event
        // to avoid opening the search while typing in the editor.
        if (!selection.isExpanded) {
            return;
        }

        const { startBlock, endBlock } = value;

        const isInFragment =
            startBlock && endBlock
                ? value.document.isInFragment(startBlock.key) ||
                  value.document.isInFragment(endBlock.key)
                : false;
        const isInLink = value.hasInline(INLINE_LINK, isInFragment);

        if (isInLink) {
            change.unwrapInline({ type: INLINE_LINK });
            change.value.decorations.forEach((range) => {
                if (range.marks?.some((mark) => mark.type === INLINE_LINK_CONTROL)) {
                    change.removeValueDecoration(range);
                }
            });
            return change;
        }

        change.setValueDecoration(
            TextRange.create({
                anchorKey: selection.anchorKey,
                anchorOffset: selection.anchorOffset,
                focusKey: selection.focusKey,
                focusOffset: selection.focusOffset,
                isBackward: selection.isBackward,
                marks: [
                    { type: SELECTION_HIGHLIGHTING_MARK, data: {} },
                    { type: INLINE_LINK_CONTROL, data: {} },
                ],
                isAtomic: true,
            })
        );

        return change;
    }

    return;
}

const INLINE_LINK_CONTROL = 'inlineLinkControl';
function renderMark(props: MarkProps) {
    const { mark } = props;
    if (mark.type === INLINE_LINK_CONTROL) {
        return <InlineLinkControlPalette {...props} />;
    }
}

function InlineLinkControlPalette(props: MarkProps) {
    const editor = useEditor();

    const hiveClient = useHiveClient();
    const contentReferenceContext = useContentReferenceContext();

    const rangeRef = React.useRef<HTMLElement>(null);
    const palette = usePaletteHandle(rangeRef, {
        onClose: () => {
            editor.change((change) => {
                change.value.decorations.forEach((range) => {
                    if (range.marks?.some((mark) => mark.type === INLINE_LINK_CONTROL)) {
                        change.removeValueDecoration(range);
                    }
                });
                change.focus();
            });
        },
    });

    // Open on mount
    useEffect(() => {
        palette.open(
            createLinkPalette({
                contentReferenceContext,
                editor,
                hiveClient,
            })
        );
    }, [palette.open, contentReferenceContext, editor, hiveClient]);

    return <span ref={rangeRef}>{props.children}</span>;
}
